/**
 * Vanilla JS Parallax implementation for CTA Image.
 * Uses IntersectionObserver to only animate elements in view,
 * and requestAnimationFrame for smooth rendering.
 */
export const initCtaImageParallax = (): void => {
  const ctaSections = document.querySelectorAll<HTMLElement>(".cta-image");

  if (ctaSections.length === 0) {
    return;
  }

  // Store elements that are currently in the viewport
  const activeElements = new Set<{
    section: HTMLElement;
    image: HTMLElement;
  }>();

  let isLooping = false;

  // The animation loop
  const updateParallax = () => {
    activeElements.forEach(({ section, image }) => {
      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Calculate position relative to the viewport center
      // 0 = center, 1 = bottom of screen, -1 = top of screen
      const position =
        (rect.top + rect.height / 2 - viewportHeight / 2) /
        (viewportHeight / 2);

      // Parallax intensity (how many pixels to move)
      // We are moving around the -25% baseline set in CSS
      const speed = 100; // pixels
      const yOffset = position * speed;

      // Apply transform using translate3d for hardware acceleration
      // We preserve the -25% baseline from CSS to keep the image centered
      image.style.transform = `translate3d(0, calc(-25% + ${yOffset}px), 0)`;
    });

    if (activeElements.size > 0) {
      requestAnimationFrame(updateParallax);
    } else {
      isLooping = false;
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const section = entry.target as HTMLElement;
        const image = section.querySelector<HTMLElement>(".cta-image__image");

        if (!image) return;

        if (entry.isIntersecting) {
          activeElements.add({ section, image });
          if (!isLooping) {
            isLooping = true;
            requestAnimationFrame(updateParallax);
          }
        } else {
          activeElements.delete({ section, image });
        }
      });
    },
    {
      rootMargin: "10% 0px", // Start slightly before element enters viewport
    }
  );

  ctaSections.forEach((section) => {
    // Ensure the image layer doesn't catch mouse events to keep scrolling smooth
    const image = section.querySelector<HTMLElement>(".cta-image__image");
    if (image) {
      image.style.pointerEvents = "none";
      image.style.willChange = "transform"; // Hint to browser
    }
    observer.observe(section);
  });

  console.log("[Parallax] CTA Image Parallax initialized (Vanilla).");
};
